import React from 'react';
import { Box, Button, Card, FormControl, FormLabel, Input, Option, Select, Stack, Typography, Table, Checkbox } from '@mui/joy';
import { DocumentWizardSharedProps, useLineManipulation } from './DocumentWizardShared';
import { ADDITIONAL_TYPE_OPTIONS } from '../types/documents.types';

interface MaterialsCardProps extends Pick<DocumentWizardSharedProps, 
  'lines' | 'setLines' | 'materials'
> {}

export const MaterialsCard: React.FC<MaterialsCardProps> = ({ lines, setLines, materials }) => {
  const { changeQtyBy, removeLine } = useLineManipulation(lines, setLines);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1, gap: 1 }}>
        <Typography level="title-md">Materials</Typography>
        <Button size="sm" onClick={() => setLines(prev => [...prev, { lineKind: 'material', materialId: undefined, name: '', qty: 1, unit: '', unitPrice: 0, lineTotal: 0 }])}>Add Material</Button>
      </Stack>
      <Box sx={{ overflowX: 'auto', width: '100%', minWidth: 800 }}>
        <style>{`
          .materials-table table {
            table-layout: fixed;
            width: 100%;
          }
        `}</style>
        <Box className="materials-table">
          <Table hoverRow>
            <thead>
              <tr>
                <th style={{ width: '300px' }}>Material</th>
                <th style={{ width: '180px' }}>Qty</th>
                <th style={{ width: '70px' }}>Unit</th>
                <th style={{ width: '100px' }}>Unit Price</th>
                <th style={{ width: '100px' }}>Total</th>
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => ({ l, idx })).filter(x => x.l.lineKind === 'material').map(x => (
                <tr key={x.idx}>
                  <td style={{ paddingRight: '8px', width: '300px' }}>
                    <Select value={x.l.materialId || ''} onChange={(_, v) => {
                      const id = String(v || '');
                      const it: any = materials.find(m => m.id === id);
                      setLines(prev => prev.map((line, i) => {
                        if (i !== x.idx) return line;
                        const updated = {
                          ...line,
                          materialId: id || undefined,
                          name: it?.name || '',
                          unit: it?.default_unit || '',
                          unitPrice: Number(it?.default_cost || 0),
                          vatInclusive: !!(it as any)?.vat_inclusive,
                        } as any;
                        return { ...updated, lineTotal: (updated.qty || 0) * (updated.unitPrice || 0) };
                      }));
                    }} placeholder="Select material" sx={{ width: '100%', maxWidth: '100%' }}>
                      {materials.map((it: any) => (<Option key={it.id} value={it.id}>{it.name}</Option>))}
                    </Select>
                  </td>
                  <td style={{ paddingLeft: '8px', paddingRight: '4px', width: '180px' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 165 }}>
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, -1)}>-</Button>
                      <Input
                        size="sm"
                        type="number"
                        value={x.l.qty ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const nextQty = Number(e.target.value || 0);
                          setLines(prev => prev.map((line, i) => {
                            if (i !== x.idx) return line;
                            return { ...line, qty: nextQty, lineTotal: (nextQty) * (line.unitPrice || 0) };
                          }));
                        }}
                        sx={{
                          width: 80,
                          textAlign: 'center',
                          mx: 0.25,
                          '& input': {
                            textAlign: 'center',
                            MozAppearance: 'textfield'
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, +1)}>+</Button>
                    </Stack>
                  </td>
                  <td style={{ paddingRight: '8px', width: '70px' }}><Input size="sm" value={x.l.unit || ''} disabled={!!x.l.materialId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unit: e.target.value } : line))} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ paddingLeft: '8px', width: '100px' }}><Input size="sm" type="number" value={String(x.l.unitPrice)} disabled={!!x.l.materialId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = Number(e.target.value || 0);
                    setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unitPrice: val, lineTotal: (line.qty || 0) * val } : line));
                  }} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ width: '100px' }}>£{Number(x.l.lineTotal ?? (x.l.qty * x.l.unitPrice)).toFixed(2)}</td>
                  <td style={{ width: '100px' }}><Button size="sm" variant="plain" color="danger" onClick={() => removeLine(x.idx)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </Box>
    </Card>
  );
};

interface LabourCardProps extends Pick<DocumentWizardSharedProps, 
  'lines' | 'setLines' | 'labourRates'
> {}

export const LabourCard: React.FC<LabourCardProps> = ({ lines, setLines, labourRates }) => {
  const { changeQtyBy, removeLine } = useLineManipulation(lines, setLines);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1, gap: 1 }}>
        <Typography level="title-md">Labour</Typography>
        <Button size="sm" onClick={() => setLines(prev => [...prev, { lineKind: 'labour', labourRateId: undefined, name: '', qty: 1, unit: '', unitPrice: 0, lineTotal: 0 }])}>Add Labour</Button>
      </Stack>
      <Box sx={{ overflowX: 'auto', width: '100%', minWidth: 800 }}>
        <style>{`
          .labour-table table {
            table-layout: fixed;
            width: 100%;
          }
        `}</style>
        <Box className="labour-table">
          <Table hoverRow>
            <thead>
              <tr>
                <th style={{ width: '300px' }}>Labour Rate</th>
                <th style={{ width: '180px' }}>Qty</th>
                <th style={{ width: '70px' }}>Unit</th>
                <th style={{ width: '100px' }}>Rate</th>
                <th style={{ width: '100px' }}>Total</th>
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => ({ l, idx })).filter(x => x.l.lineKind === 'labour').map(x => (
                <tr key={x.idx}>
                  <td style={{ paddingRight: '8px', width: '300px' }}>
                    <Select value={x.l.labourRateId || ''} onChange={(_, v) => {
                      const id = String(v || '');
                      const it: any = labourRates.find(r => r.id === id);
                      setLines(prev => prev.map((line, i) => {
                        if (i !== x.idx) return line;
                        const updated = {
                          ...line,
                          labourRateId: id || undefined,
                          name: it?.name || '',
                          unit: it?.rate_type || '',
                          unitPrice: Number(it?.rate || 0),
                        } as any;
                        return { ...updated, lineTotal: (updated.qty || 0) * (updated.unitPrice || 0) };
                      }));
                    }} placeholder="Select labour rate" sx={{ width: '100%', maxWidth: '100%' }}>
                      {labourRates.map((it: any) => (<Option key={it.id} value={it.id}>{it.name}</Option>))}
                    </Select>
                  </td>
                  <td style={{ paddingLeft: '8px', paddingRight: '4px', width: '180px' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 165 }}>
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, -1)}>-</Button>
                      <Input
                        size="sm"
                        type="number"
                        value={x.l.qty ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const nextQty = Number(e.target.value || 0);
                          setLines(prev => prev.map((line, i) => {
                            if (i !== x.idx) return line;
                            return { ...line, qty: nextQty, lineTotal: (nextQty) * (line.unitPrice || 0) };
                          }));
                        }}
                        sx={{
                          width: 80,
                          textAlign: 'center',
                          mx: 0.25,
                          '& input': {
                            textAlign: 'center',
                            MozAppearance: 'textfield'
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, +1)}>+</Button>
                    </Stack>
                  </td>
                  <td style={{ paddingRight: '8px', width: '70px' }}><Input size="sm" value={x.l.unit || ''} disabled={!!x.l.labourRateId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unit: e.target.value } : line))} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ paddingLeft: '8px', width: '100px' }}><Input size="sm" type="number" value={String(x.l.unitPrice)} disabled={!!x.l.labourRateId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = Number(e.target.value || 0);
                    setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unitPrice: val, lineTotal: (line.qty || 0) * val } : line));
                  }} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ width: '100px' }}>£{Number(x.l.lineTotal ?? (x.l.qty * x.l.unitPrice)).toFixed(2)}</td>
                  <td style={{ width: '100px' }}><Button size="sm" variant="plain" color="danger" onClick={() => removeLine(x.idx)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </Box>
    </Card>
  );
};

interface EquipmentCardProps extends Pick<DocumentWizardSharedProps, 
  'lines' | 'setLines' | 'equipment'
> {}

export const EquipmentCard: React.FC<EquipmentCardProps> = ({ lines, setLines, equipment }) => {
  const { changeQtyBy, removeLine } = useLineManipulation(lines, setLines);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1, gap: 1 }}>
        <Typography level="title-md">Equipment</Typography>
        <Button size="sm" onClick={() => setLines(prev => [...prev, { lineKind: 'equipment', equipmentId: undefined, name: '', qty: 1, unit: '', unitPrice: 0, lineTotal: 0 }])}>Add Equipment</Button>
      </Stack>
      <Box sx={{ overflowX: 'auto', width: '100%', minWidth: 800 }}>
        <style>{`
          .equipment-table table {
            table-layout: fixed;
            width: 100%;
          }
        `}</style>
        <Box className="equipment-table">
          <Table hoverRow>
            <thead>
              <tr>
                <th style={{ width: '300px' }}>Equipment</th>
                <th style={{ width: '180px' }}>Qty</th>
                <th style={{ width: '70px' }}>Unit</th>
                <th style={{ width: '100px' }}>Unit Price</th>
                <th style={{ width: '100px' }}>Total</th>
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => ({ l, idx })).filter(x => x.l.lineKind === 'equipment').map(x => (
                <tr key={x.idx}>
                  <td style={{ paddingRight: '8px', width: '300px' }}>
                    <Select value={x.l.equipmentId || ''} onChange={(_, v) => {
                      const id = String(v || '');
                      const it: any = equipment.find(e => e.id === id);
                      setLines(prev => prev.map((line, i) => {
                        if (i !== x.idx) return line;
                        const updated = {
                          ...line,
                          equipmentId: id || undefined,
                          name: it?.name || '',
                          unit: it?.default_unit || '',
                          unitPrice: Number(it?.default_cost || 0),
                          vatInclusive: !!(it as any)?.vat_inclusive,
                        } as any;
                        return { ...updated, lineTotal: (updated.qty || 0) * (updated.unitPrice || 0) };
                      }));
                    }} placeholder="Select equipment" sx={{ width: '100%', maxWidth: '100%' }}>
                      {equipment.map((it: any) => (<Option key={it.id} value={it.id}>{it.name}</Option>))}
                    </Select>
                  </td>
                  <td style={{ paddingLeft: '8px', paddingRight: '4px', width: '180px' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 165 }}>
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, -1)}>-</Button>
                      <Input
                        size="sm"
                        type="number"
                        value={x.l.qty ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const nextQty = Number(e.target.value || 0);
                          setLines(prev => prev.map((line, i) => {
                            if (i !== x.idx) return line;
                            return { ...line, qty: nextQty, lineTotal: (nextQty) * (line.unitPrice || 0) };
                          }));
                        }}
                        sx={{
                          width: 80,
                          textAlign: 'center',
                          mx: 0.25,
                          '& input': {
                            textAlign: 'center',
                            MozAppearance: 'textfield'
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, +1)}>+</Button>
                    </Stack>
                  </td>
                  <td style={{ paddingRight: '8px', width: '70px' }}><Input size="sm" value={x.l.unit || ''} disabled={!!x.l.equipmentId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unit: e.target.value } : line))} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ paddingLeft: '8px', width: '100px' }}><Input size="sm" type="number" value={String(x.l.unitPrice)} disabled={!!x.l.equipmentId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = Number(e.target.value || 0);
                    setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unitPrice: val, lineTotal: (line.qty || 0) * val } : line));
                  }} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ width: '100px' }}>£{Number(x.l.lineTotal ?? (x.l.qty * x.l.unitPrice)).toFixed(2)}</td>
                  <td style={{ width: '100px' }}><Button size="sm" variant="plain" color="danger" onClick={() => removeLine(x.idx)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </Box>
    </Card>
  );
};

interface AdditionalCostsCardProps extends Pick<DocumentWizardSharedProps, 
  'lines' | 'setLines'
> {}

export const AdditionalCostsCard: React.FC<AdditionalCostsCardProps> = ({ lines, setLines }) => {
  const { changeQtyBy, removeLine } = useLineManipulation(lines, setLines);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1, gap: 1 }}>
        <Typography level="title-md">Additional Costs</Typography>
        <Button size="sm" onClick={() => setLines(prev => [...prev, { lineKind: 'other', additionalType: null, name: '', qty: 1, unit: '', unitPrice: 0, lineTotal: 0 }])}>Add Additional Cost</Button>
      </Stack>
      <Box sx={{ overflowX: 'auto', width: '100%', minWidth: 900 }}>
        <style>{`
          .additional-costs-table table {
            table-layout: fixed;
            width: 100%;
          }
        `}</style>
        <Box className="additional-costs-table">
          <Table hoverRow>
            <thead>
              <tr>
                <th style={{ width: '140px' }}>Type</th>
                <th style={{ width: '210px' }}>Name</th>
                <th style={{ width: '170px' }}>Qty</th>
                <th style={{ width: '56px' }}>Unit</th>
                <th style={{ width: '88px' }}>Unit Price</th>
                <th style={{ width: '88px' }}>VAT inclusive?</th>
                <th style={{ width: '90px' }}>Total</th>
                <th style={{ width: '72px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lines
                .map((l, idx) => ({ l, idx }))
                .filter(x => x.l.lineKind === 'other' && (x.l as any).metaJson?.feature !== 'visual_designer')
                .map(x => (
                <tr key={x.idx}>
                  <td style={{ paddingRight: '8px', width: '140px' }}>
                    <Select
                      size="sm"
                      value={x.l.additionalType ?? null}
                      onChange={(_, value) => {
                        setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, additionalType: value ?? null } : line));
                      }}
                      placeholder="Select type"
                      sx={{ width: '100%', maxWidth: '100%' }}
                    >
                      {ADDITIONAL_TYPE_OPTIONS.map((option) => (
                        <Option key={option.value} value={option.value}>{option.label}</Option>
                      ))}
                    </Select>
                  </td>
                  <td style={{ paddingRight: '8px', width: '210px' }}><Input size="sm" value={x.l.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, name: e.target.value } : line))} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ paddingLeft: '8px', paddingRight: '4px', width: '170px' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 165 }}>
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, -1)}>-</Button>
                      <Input
                        size="sm"
                        type="number"
                        value={x.l.qty ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const nextQty = Number(e.target.value || 0);
                          setLines(prev => prev.map((line, i) => {
                            if (i !== x.idx) return line;
                            return { ...line, qty: nextQty, lineTotal: (nextQty) * (line.unitPrice || 0) };
                          }));
                        }}
                        sx={{
                          width: 80,
                          textAlign: 'center',
                          mx: 0.25,
                          '& input': {
                            textAlign: 'center',
                            MozAppearance: 'textfield'
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                      <Button size="sm" variant="outlined" onClick={() => changeQtyBy(x.idx, +1)}>+</Button>
                    </Stack>
                  </td>
                  <td style={{ paddingRight: '8px', width: '56px' }}><Input size="sm" value={x.l.unit || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unit: e.target.value } : line))} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ paddingLeft: '8px', width: '88px' }}><Input size="sm" type="number" value={String(x.l.unitPrice)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = Number(e.target.value || 0);
                    setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, unitPrice: val, lineTotal: (line.qty || 0) * val } : line));
                  }} sx={{ width: '100%', maxWidth: '100%' }} /></td>
                  <td style={{ width: '88px' }}><Checkbox checked={(x.l as any).vatInclusive || false} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLines(prev => prev.map((line, i) => i === x.idx ? { ...line, vatInclusive: e.target.checked } as any : line))} /></td>
                  <td style={{ width: '90px' }}>£{Number(x.l.lineTotal ?? (x.l.qty * x.l.unitPrice)).toFixed(2)}</td>
                  <td style={{ width: '72px' }}><Button size="sm" variant="plain" color="danger" onClick={() => removeLine(x.idx)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </Box>
    </Card>
  );
};

interface DurationCardProps extends Pick<DocumentWizardSharedProps, 
  'displayDays' | 'setDisplayDays' | 'displayHours' | 'setDisplayHours'
> {}

export const DurationCard: React.FC<DurationCardProps> = ({ displayDays, setDisplayDays, displayHours, setDisplayHours }) => {
  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
      <Typography level="title-sm" sx={{ mb: 2 }}>Days/Hours to represent on Estimate/Quote</Typography>
      <Typography level="body-xs" sx={{ mb: 2, color: 'text.secondary' }}>
        These fields are for display purposes only and don't affect pricing calculations.
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl>
          <FormLabel>Days</FormLabel>
          <Input
            type="number"
            size="sm"
            sx={{ width: 100 }}
            value={displayDays || ''}
            disabled={!!displayHours}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const days = e.target.value ? Number(e.target.value) : null;
              setDisplayDays(days);
              if (days) {
                setDisplayHours(null);
              }
            }}
            placeholder="0"
          />
        </FormControl>
        <Typography level="body-sm" sx={{ mx: 1, color: 'text.secondary' }}>OR</Typography>
        <FormControl>
          <FormLabel>Hours</FormLabel>
          <Input
            type="number"
            size="sm"
            sx={{ width: 100 }}
            value={displayHours || ''}
            disabled={!!displayDays}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const hours = e.target.value ? Number(e.target.value) : null;
              setDisplayHours(hours);
              if (hours) {
                setDisplayDays(null);
              }
            }}
            placeholder="0"
          />
        </FormControl>
      </Stack>
    </Box>
  );
};
